// SwiftLaTeX WASM integration for browser-side compilation
// This module provides a lightweight interface to the SwiftLaTeX engine
// for instant preview compilation of simple LaTeX documents.

export interface SwiftLatexOptions {
  wasmUrl?: string;
  onReady?: () => void;
  onError?: (error: string) => void;
}

export interface CompileResult {
  success: boolean;
  pdf?: Uint8Array;
  log: string;
  errors: CompileError[];
  durationMs: number;
}

export interface CompileError {
  line: number;
  message: string;
  file?: string;
}

let engineReady = false;
let engineLoading = false;
let pdflatexEngine: any = null;

// Parse LaTeX log for errors
function parseLatexLog(log: string): CompileError[] {
  const errors: CompileError[] = [];
  const lines = log.split('\n');

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // Match "! Error message" pattern
    const errorMatch = line.match(/^! (.+)$/);
    if (errorMatch) {
      let lineNum = 0;
      // Look for "l.123" in nearby lines
      for (let j = i; j < Math.min(i + 5, lines.length); j++) {
        const lineMatch = lines[j].match(/^l\.(\d+)/);
        if (lineMatch) {
          lineNum = parseInt(lineMatch[1], 10);
          break;
        }
      }
      errors.push({
        line: lineNum,
        message: errorMatch[1],
      });
    }

    // Match "LaTeX Error:" pattern
    const latexErrorMatch = line.match(/^(.+):(\d+): (.+)$/);
    if (latexErrorMatch) {
      errors.push({
        line: parseInt(latexErrorMatch[2], 10),
        message: latexErrorMatch[3],
        file: latexErrorMatch[1],
      });
    }
  }

  return errors;
}

export async function initSwiftLatex(options: SwiftLatexOptions = {}): Promise<boolean> {
  if (engineReady) return true;
  if (engineLoading) {
    // Wait for loading to complete
    return new Promise((resolve) => {
      const check = setInterval(() => {
        if (engineReady) {
          clearInterval(check);
          resolve(true);
        }
      }, 100);
      // Timeout after 30s
      setTimeout(() => {
        clearInterval(check);
        resolve(false);
      }, 30000);
    });
  }

  engineLoading = true;

  try {
    // SwiftLaTeX loads as a global when its script is included
    // For now, we'll use a dynamic import approach
    const wasmUrl = options.wasmUrl || '/js/latex-editor/PdfTeXEngine.js';

    // Check if SwiftLaTeX global is available
    if ((window as any).PdfTeXEngine) {
      pdflatexEngine = new (window as any).PdfTeXEngine();
      await pdflatexEngine.loadEngine();
      engineReady = true;
      if (options.onReady) options.onReady();
      console.log('[SwiftLaTeX] Engine ready');
      return true;
    }

    // Try loading the script dynamically
    await new Promise<void>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = wasmUrl;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load SwiftLaTeX'));
      document.head.appendChild(script);
    });

    if ((window as any).PdfTeXEngine) {
      pdflatexEngine = new (window as any).PdfTeXEngine();
      await pdflatexEngine.loadEngine();
      engineReady = true;
      if (options.onReady) options.onReady();
      console.log('[SwiftLaTeX] Engine ready');
      return true;
    }

    throw new Error('PdfTeXEngine not available after script load');
  } catch (e: any) {
    console.warn('[SwiftLaTeX] Init failed:', e.message);
    if (options.onError) options.onError(e.message);
    engineLoading = false;
    return false;
  }
}

export async function compileLocally(
  mainContent: string,
  files: { path: string; content: string }[] = []
): Promise<CompileResult> {
  const startTime = performance.now();

  if (!engineReady || !pdflatexEngine) {
    return {
      success: false,
      log: 'SwiftLaTeX engine not initialized',
      errors: [{ line: 0, message: 'Browser LaTeX engine not available. Using server compilation.' }],
      durationMs: 0,
    };
  }

  try {
    // Write files to virtual filesystem
    pdflatexEngine.writeMemFSFile('main.tex', mainContent);
    for (const file of files) {
      if (!file.path.match(/\.(png|jpg|jpeg|gif|pdf)$/i)) {
        pdflatexEngine.writeMemFSFile(file.path, file.content);
      }
    }

    // Set main file and compile
    pdflatexEngine.setEngineMainFile('main.tex');
    const result = await pdflatexEngine.compileLaTeX();

    const durationMs = Math.round(performance.now() - startTime);
    const log = result.log || '';
    const errors = parseLatexLog(log);

    if (result.status === 0 && result.pdf) {
      return {
        success: true,
        pdf: new Uint8Array(result.pdf),
        log,
        errors,
        durationMs,
      };
    } else {
      return {
        success: false,
        log,
        errors: errors.length > 0 ? errors : [{ line: 0, message: 'Compilation failed' }],
        durationMs,
      };
    }
  } catch (e: any) {
    const durationMs = Math.round(performance.now() - startTime);
    return {
      success: false,
      log: e.message || 'Unknown compilation error',
      errors: [{ line: 0, message: e.message || 'Compilation failed' }],
      durationMs,
    };
  }
}

export function isEngineReady(): boolean {
  return engineReady;
}
